import type { ReactNode } from 'react'

type IconProps = { className?: string }

const defaults = {
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  'aria-hidden': true,
}

function Icon({
  className = 'h-4 w-4 shrink-0',
  children,
}: IconProps & { children: ReactNode }) {
  return (
    <svg className={className} {...defaults}>
      {children}
    </svg>
  )
}

export function IconPlus({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconClear({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M6.75 6.75 17.25 17.25M17.25 6.75 6.75 17.25"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconLayoutGrid({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M5.75 5.75h4.5v4.5h-4.5v-4.5ZM13.75 5.75h4.5v4.5h-4.5v-4.5ZM5.75 13.75h4.5v4.5h-4.5v-4.5ZM13.75 13.75h4.5v4.5h-4.5v-4.5Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconList({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M6.75 7.75h10.5M6.75 12h10.5M6.75 16.25h10.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconUserPlus({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M16.5 7.5a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM5.75 19.25a6.25 6.25 0 0 1 12.5 0M19.25 8.25v4M21.25 10.25h-4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconBell({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M12 5.25a4.25 4.25 0 0 0-4.25 4.25v2.5l-1.5 2.75h11.5l-1.5-2.75v-2.5A4.25 4.25 0 0 0 12 5.25ZM10 18.75h4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconEye({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M2.25 12s3.75-7.5 9.75-7.5S21.75 12 21.75 12s-3.75 7.5-9.75 7.5S2.25 12 2.25 12Z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <circle cx="12" cy="12" r="2.75" stroke="currentColor" strokeWidth="1.75" />
    </Icon>
  )
}

export function IconEyeOff({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconGlobe({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M2.25 12h19.5M12 2.25c2.5 2.75 3.75 6.2 3.75 9.75S14.5 18.95 12 21.75M12 2.25C9.5 5 8.25 8.45 8.25 12s1.25 7 3.75 9.75M12 2.25v19.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconEdit({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconTrash({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M4.75 6.25h14.5M9.75 9.75v6.5M14.25 9.75v6.5M6.75 6.25l.75 12A2.25 2.25 0 0 0 9.75 20.5h4.5a2.25 2.25 0 0 0 2.25-2.25l.75-12M9 6.25V4.75A1.25 1.25 0 0 1 10.25 3.5h3.5A1.25 1.25 0 0 1 15 4.75v1.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

/** Γρήγορη AI προσφορά (capture). */
export function IconSparkles({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

/** Ημερολόγιο / ραντεβού. */
export function IconCalendar({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M6.75 3v2.25M17.25 3v2.25M4.5 8.25h15M4.5 19.5h15a1.5 1.5 0 001.5-1.5V8.25a1.5 1.5 0 00-1.5-1.5h-15a1.5 1.5 0 00-1.5 1.5v9.75a1.5 1.5 0 001.5 1.5z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

/** Εξαγωγή / λήψη PDF. */
export function IconPdf({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M7.5 3.75h6.75L18.75 8.25v11.25a1.5 1.5 0 0 1-1.5 1.5h-10.5a1.5 1.5 0 0 1-1.5-1.5V5.25a1.5 1.5 0 0 1 1.5-1.5Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
      <path
        d="M14.25 3.75v4.5h4.5M8.25 12.75h7.5M8.25 15.75h4.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

/** Προχωρημένη / πλήρης φόρμα. */
export function IconSliders({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M10.5 6.75h9.75M10.5 12h9.75M10.5 17.25h9.75M6 6.75h.008v.008H6V6.75zm0 5.25h.008v.008H6V12zm0 5.25h.008v.008H6V17.25z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}
